import React, { useEffect, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, KpiCard, EmptyState } from "@/components/ui-parts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";

const REPORTS = [
  { key: "production", label: "Production" },
  { key: "electricity", label: "Electricity" },
  { key: "inventory", label: "Inventory" },
  { key: "packing", label: "Packing" },
  { key: "spare", label: "Spare" },
  { key: "sauda", label: "Sauda" },
];

export default function Reports() {
  const { user } = useAuth();
  const [tab, setTab] = useState("production");
  const [unitFilter, setUnitFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const availableUnits = user?.role === "admin" ? Object.keys(UNIT_META) : (user?.assigned_units || []);

  const params = () => (unitFilter === "all" ? {} : { unit: unitFilter });

  const load = async () => {
    setLoading(true);
    try {
      const [s, tabData] = await Promise.all([
        api.get("/reports/summary", { params: params() }),
        fetchTab(tab, params()),
      ]);
      setSummary(s.data);
      setRows(tabData);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [tab, unitFilter]);

  return (
    <div>
      <PageHeader
        eyebrow="Analytics"
        title="Business Reports"
        description="Cross-unit reports for production, electricity, inventory, packing, spare and sauda — filterable by unit."
        testid="reports-header"
      />

      <div className="flex gap-2 mb-6 flex-wrap items-center" data-testid="unit-filter">
        <span className="label-caps mr-2">Unit filter</span>
        <button onClick={() => setUnitFilter("all")} data-testid="unit-all" className={`px-4 py-2 label-caps text-[10px] border ${unitFilter === "all" ? "bg-amber-400 text-black border-amber-400" : "border-zinc-800 text-zinc-400"}`}>All</button>
        {availableUnits.map((u) => (
          <button key={u} onClick={() => setUnitFilter(u)} data-testid={`unit-${u}`} className={`px-4 py-2 label-caps text-[10px] border ${unitFilter === u ? "bg-amber-400 text-black border-amber-400" : "border-zinc-800 text-zinc-400"}`}>
            {UNIT_META[u]?.tag}
          </button>
        ))}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard label="Output (kg)" tone="amber" value={fmt(summary.totals.production_output_kg)} />
          <KpiCard label="Yield %" tone="success" value={`${summary.totals.yield_pct}%`} />
          <KpiCard label="Wastage (kg)" tone="danger" value={fmt(summary.totals.wastage_kg)} />
          <KpiCard label="kWh / kg" value={summary.totals.kwh_per_kg || 0} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="rounded-none bg-[#0e0e11] border border-zinc-800 h-auto p-0 flex-wrap">
          {REPORTS.map((r) => (
            <TabsTrigger key={r.key} value={r.key} data-testid={`report-tab-${r.key}`} className="rounded-none data-[state=active]:bg-amber-400 data-[state=active]:text-black px-6 py-2 label-caps text-[10px]">
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "production" && (
        <Panel title="Production breakdown">
          {loading ? <div className="p-6 text-xs text-zinc-500">Loading…</div> : <ProductionReport rows={rows} />}
        </Panel>
      )}
      {tab === "electricity" && (
        <Panel title="Electricity breakdown">
          {loading ? <div className="p-6 text-xs text-zinc-500">Loading…</div> : <ElectricityReport rows={rows} />}
        </Panel>
      )}
      {tab === "inventory" && (
        <Panel title="Inventory (raw + finished)">
          <SimpleInvTable rows={rows.filter((r) => ["raw", "finished"].includes(r.category))} />
        </Panel>
      )}
      {tab === "packing" && (
        <Panel title="Packing material">
          <SimpleInvTable rows={rows.filter((r) => r.category === "packing")} />
        </Panel>
      )}
      {tab === "spare" && (
        <Panel title="Spare parts">
          <SimpleInvTable rows={rows.filter((r) => r.category === "spare")} />
        </Panel>
      )}
      {tab === "sauda" && (
        <Panel title="Sauda register">
          <SaudaReport rows={rows} />
        </Panel>
      )}
    </div>
  );
}

async function fetchTab(tab, params) {
  if (tab === "production") return (await api.get("/production", { params })).data;
  if (tab === "electricity") return (await api.get("/electricity", { params })).data;
  if (["inventory", "packing", "spare"].includes(tab)) {
    return (await api.get("/inventory", { params })).data;
  }
  if (tab === "sauda") return (await api.get("/sauda", { params })).data;
  return [];
}

function ProductionReport({ rows }) {
  if (!rows.length) return <EmptyState message="No production" />;
  const byProduct = {};
  rows.forEach((r) => {
    byProduct[r.product] = byProduct[r.product] || { product: r.product, output: 0, wastage: 0 };
    byProduct[r.product].output += r.output_kg || 0;
    byProduct[r.product].wastage += r.wastage_kg || 0;
  });
  const data = Object.values(byProduct);
  return (
    <div>
      <div className="h-64 p-4 border-b border-zinc-800">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid stroke="#27272a" strokeDasharray="2 4" />
            <XAxis dataKey="product" stroke="#71717a" fontSize={10} />
            <YAxis stroke="#71717a" fontSize={10} />
            <Tooltip contentStyle={{ background: "#0e0e11", border: "1px solid #27272a", fontSize: 12 }} />
            <Bar dataKey="output" fill="#FFB224" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800">
            <TableHead className="label-caps">Date</TableHead>
            <TableHead className="label-caps">Unit</TableHead>
            <TableHead className="label-caps">Product</TableHead>
            <TableHead className="label-caps text-right">Input</TableHead>
            <TableHead className="label-caps text-right">Output</TableHead>
            <TableHead className="label-caps text-right">Wastage</TableHead>
            <TableHead className="label-caps text-right">Yield %</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-zinc-800">
              <TableCell className="font-mono text-xs">{r.date}</TableCell>
              <TableCell><span style={{ color: UNIT_META[r.unit]?.accent }} className="label-caps">{UNIT_META[r.unit]?.tag}</span></TableCell>
              <TableCell>{r.product}</TableCell>
              <TableCell className="text-right font-mono">{r.raw_input_kg}</TableCell>
              <TableCell className="text-right font-mono text-amber-400">{r.output_kg}</TableCell>
              <TableCell className="text-right font-mono">{r.wastage_kg}</TableCell>
              <TableCell className="text-right font-mono">{r.raw_input_kg ? ((r.output_kg / r.raw_input_kg) * 100).toFixed(1) : "-"}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ElectricityReport({ rows }) {
  if (!rows.length) return <EmptyState message="No readings" />;
  return (
    <Table>
      <TableHeader>
        <TableRow className="border-zinc-800">
          <TableHead className="label-caps">Date</TableHead>
          <TableHead className="label-caps">Unit</TableHead>
          <TableHead className="label-caps text-right">Consumed</TableHead>
          <TableHead className="label-caps text-right">DG</TableHead>
          <TableHead className="label-caps text-right">Rate</TableHead>
          <TableHead className="label-caps text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} className="border-zinc-800">
            <TableCell className="font-mono text-xs">{r.date}</TableCell>
            <TableCell><span style={{ color: UNIT_META[r.unit]?.accent }} className="label-caps">{UNIT_META[r.unit]?.tag}</span></TableCell>
            <TableCell className="text-right font-mono text-amber-400">{r.units_consumed}</TableCell>
            <TableCell className="text-right font-mono">{r.dg_units}</TableCell>
            <TableCell className="text-right font-mono">{r.rate_per_unit}</TableCell>
            <TableCell className="text-right font-mono">₹{r.total_cost?.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SimpleInvTable({ rows }) {
  if (!rows.length) return <EmptyState message="No records" />;
  return (
    <Table>
      <TableHeader>
        <TableRow className="border-zinc-800">
          <TableHead className="label-caps">Date</TableHead>
          <TableHead className="label-caps">Unit</TableHead>
          <TableHead className="label-caps">Item</TableHead>
          <TableHead className="label-caps">Category</TableHead>
          <TableHead className="label-caps text-right">Closing (kg)</TableHead>
          <TableHead className="label-caps text-right">Rate</TableHead>
          <TableHead className="label-caps text-right">Value</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} className="border-zinc-800">
            <TableCell className="font-mono text-xs">{r.date}</TableCell>
            <TableCell><span style={{ color: UNIT_META[r.unit]?.accent }} className="label-caps">{UNIT_META[r.unit]?.tag}</span></TableCell>
            <TableCell>{r.item}</TableCell>
            <TableCell className="uppercase text-xs">{r.category}</TableCell>
            <TableCell className="text-right font-mono text-amber-400">{r.closing_stock_kg}</TableCell>
            <TableCell className="text-right font-mono">{r.rate}</TableCell>
            <TableCell className="text-right font-mono">₹{(r.closing_stock_kg * r.rate).toFixed(0)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SaudaReport({ rows }) {
  if (!rows.length) return <EmptyState message="No sauda" />;
  return (
    <Table>
      <TableHeader>
        <TableRow className="border-zinc-800">
          <TableHead className="label-caps">Sauda No</TableHead>
          <TableHead className="label-caps">Date</TableHead>
          <TableHead className="label-caps">Unit</TableHead>
          <TableHead className="label-caps">Type</TableHead>
          <TableHead className="label-caps">Party</TableHead>
          <TableHead className="label-caps">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} className="border-zinc-800">
            <TableCell className="font-mono text-xs text-amber-400">{r.sauda_no}</TableCell>
            <TableCell className="font-mono text-xs">{r.date}</TableCell>
            <TableCell><span style={{ color: UNIT_META[r.unit]?.accent }} className="label-caps">{UNIT_META[r.unit]?.tag}</span></TableCell>
            <TableCell className="uppercase text-xs">{r.type}</TableCell>
            <TableCell>{r.party_name}</TableCell>
            <TableCell className="uppercase text-xs">{r.status}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function fmt(n) {
  if (n === undefined || n === null) return "0";
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 1 });
}
